import { useState, useEffect } from 'react';
import { jobsApi, type Job, type JobsResponse } from '../lib/api';

const SOURCES = [
  { value: '', label: 'All Sources' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'greenhouse', label: 'Greenhouse' },
  { value: 'lever', label: 'Lever' },
  { value: 'ashby', label: 'Ashby' },
  { value: 'hiringcafe', label: 'HiringCafe' },
];

const SCORE_FILTERS = [
  { value: 0, label: 'All Scores' },
  { value: 70, label: '70%+ (High Match)' },
  { value: 80, label: '80%+ (Very High)' },
  { value: 90, label: '90%+ (Excellent)' },
];

export default function JobsList() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [sourceFilter, setSourceFilter] = useState('');
  const [scoreFilter, setScoreFilter] = useState(0);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  useEffect(() => {
    loadJobs();
  }, [pagination.page, sourceFilter, scoreFilter]);

  async function loadJobs() {
    setLoading(true);
    setError(null);

    try {
      const response: JobsResponse = await jobsApi.list({
        page: pagination.page,
        pageSize: pagination.pageSize,
        source: sourceFilter || undefined,
        minScore: scoreFilter || undefined,
      });

      setJobs(response.jobs);
      setPagination({
        ...pagination,
        total: response.total,
        totalPages: response.totalPages,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }

  function getScoreColor(score: number | null): string {
    if (!score) return 'bg-gray-100 text-gray-600';
    if (score >= 90) return 'bg-green-100 text-green-800';
    if (score >= 80) return 'bg-blue-100 text-blue-800';
    if (score >= 70) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-600';
  }

  function formatSalary(min: number | null, max: number | null): string {
    if (!min && !max) return '';
    const format = (n: number) => `$${(n / 1000).toFixed(0)}k`;
    if (min && max) return `${format(min)} - ${format(max)}`;
    if (min) return `${format(min)}+`;
    if (max) return `Up to ${format(max)}`;
    return '';
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Job Matches</h1>
        <div className="flex flex-wrap gap-3">
          <select
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setPagination({ ...pagination, page: 1 });
            }}
            className="input w-auto"
          >
            {SOURCES.map((source) => (
              <option key={source.value} value={source.value}>
                {source.label}
              </option>
            ))}
          </select>
          <select
            value={scoreFilter}
            onChange={(e) => {
              setScoreFilter(parseInt(e.target.value));
              setPagination({ ...pagination, page: 1 });
            }}
            className="input w-auto"
          >
            {SCORE_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner border-primary-600 border-t-transparent h-8 w-8" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="card text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No jobs found</h3>
          <p className="mt-2 text-gray-500">
            {scoreFilter || sourceFilter
              ? 'Try adjusting your filters'
              : 'Run a job search from the dashboard to find matching jobs'}
          </p>
        </div>
      ) : (
        <>
          {/* Jobs list */}
          <div className="space-y-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                className={`card cursor-pointer hover:border-primary-300 transition-colors ${
                  selectedJob?.id === job.id ? 'border-primary-500 ring-1 ring-primary-500' : ''
                }`}
                onClick={() => setSelectedJob(selectedJob?.id === job.id ? null : job)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <a
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-lg font-semibold text-gray-900 hover:text-primary-600 truncate"
                      >
                        {job.title || 'Untitled Position'}
                      </a>
                      {job.notified === 1 && (
                        <span className="badge bg-gray-100 text-gray-600 text-xs">Notified</span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                      <span className="font-medium text-gray-700">{job.company || 'Unknown Company'}</span>
                      {job.location && (
                        <>
                          <span>•</span>
                          <span>{job.location}</span>
                        </>
                      )}
                      {job.source && (
                        <>
                          <span>•</span>
                          <span className="capitalize">{job.source}</span>
                        </>
                      )}
                    </div>
                    {(job.salary_min || job.salary_max) && (
                      <p className="mt-1 text-sm text-green-600 font-medium">
                        {formatSalary(job.salary_min, job.salary_max)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`badge text-sm font-semibold ${getScoreColor(job.match_score)}`}>
                      {job.match_score ? `${job.match_score}%` : 'N/A'}
                    </span>
                    {job.date_posted && (
                      <span className="text-xs text-gray-400">
                        {new Date(job.date_posted).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Expanded details */}
                {selectedJob?.id === job.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {job.match_reason && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700">Match Reason</h4>
                        <p className="mt-1 text-sm text-gray-600">{job.match_reason}</p>
                      </div>
                    )}
                    {job.description_preview && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Description Preview</h4>
                        <p className="mt-1 text-sm text-gray-600">{job.description_preview}</p>
                      </div>
                    )}
                    <div className="mt-4 flex gap-3">
                      <a
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary text-sm"
                      >
                        View Full Job Posting
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.pageSize + 1} to{' '}
                {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} jobs
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                  disabled={pagination.page === 1}
                  className="btn btn-secondary text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                  disabled={pagination.page === pagination.totalPages}
                  className="btn btn-secondary text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
